import {
  Avatar,
  AvatarProps,
  ChakraBox,
  ChakraFlex,
  DateFormat,
  FAIconName,
  FireflyButton,
  Icon,
  Text,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

interface AppointmentInfoProps {
  icon: FAIconName;
  id: string;
  label: string;
}

const AppointmentInfo = ({ icon, id, label }: AppointmentInfoProps) => (
  <ChakraFlex alignItems="center" gap="4px">
    <Icon aria-hidden color="neutral.secondary" name={icon} />
    <Text color="neutral.secondary" id={id} variant="caption">
      {label}
    </Text>
  </ChakraFlex>
);

interface AppointmentCardProps {
  /** Optional props for rendering an avatar on the appointment card */
  avatarProps?: Pick<AvatarProps, 'name' | 'src' | 'userType'>;
  /** Optional badge to display before the avatar (e.g., "Proposed" tag) */
  badge?: React.ReactNode;
  endTimeISO: string;
  /** Optional formatting instructions for the appointment date. If not provided, defaults to 'EEEE, MMM dd' (e.g. "Wednesday, Sep 08"). */
  formatDate?: (isoString: string) => string;
  /** Optional formatting instructions for the appointment start and end time. If not provided, defaults to 'hh:mm a - hh:mm a' (e.g. "09:00 AM - 10:00 AM"). */
  formatStartEndTime?: (startIso: string, endIso: string) => string;
  id: string;
  /** Renders as a borderless row for a parent section card to hold. */
  isFlat?: boolean;
  /** Flat layout only: renders a Join button that calls this. */
  onJoin?: () => void;
  /** Optional recurrence label to display (e.g., "Every Tuesday", "Every other Monday") */
  recurrenceLabel?: string;
  startTimeISO: string;
  title: string;
}

const AppointmentCard = ({
  avatarProps,
  badge,
  endTimeISO,
  formatDate,
  formatStartEndTime,
  id,
  isFlat = false,
  onJoin,
  recurrenceLabel,
  startTimeISO,
  title,
}: AppointmentCardProps) => {
  const dateLabel =
    formatDate?.(startTimeISO) ??
    DateTime.fromISO(startTimeISO).toFormat(DateFormat.weekdayShortMonthDate);
  const timeLabel =
    formatStartEndTime?.(startTimeISO, endTimeISO) ??
    `${DateTime.fromISO(startTimeISO).toFormat(DateFormat.time)} - ${DateTime.fromISO(endTimeISO).toFormat(DateFormat.time)}`;

  if (isFlat) {
    return (
      <ChakraFlex alignItems="center" gap="18px" id={`${id}__container`}>
        <ChakraFlex direction="column" flex="1" gap="2px" minWidth={0}>
          <Text
            color="neutral.primary"
            id={`${id}__title`}
            variant="captionMedium"
          >
            {title}
          </Text>
          <Text color="neutral.secondary" id={`${id}__when`} variant="caption">
            {[dateLabel, timeLabel, recurrenceLabel].filter(Boolean).join(', ')}
          </Text>
        </ChakraFlex>
        <ChakraFlex alignItems="center" flexShrink={0} gap="12px">
          {onJoin && (
            <FireflyButton
              ariaLabel={`Join ${title}`}
              id={`${id}__join`}
              leftIcon={FAIconName.externalLinkRegular}
              onClick={onJoin}
              size="small"
              variant="primary"
            >
              Join
            </FireflyButton>
          )}
          {badge}
          {avatarProps && <Avatar {...avatarProps} id={id} size="xs" />}
        </ChakraFlex>
      </ChakraFlex>
    );
  }

  return (
    <ChakraBox
      borderColor="neutral.line"
      borderRadius="12px"
      borderWidth="1px"
      id={`${id}__container`}
      minHeight="79px"
      padding="18px"
    >
      <ChakraFlex direction="column" gap="6px">
        <ChakraFlex
          alignItems="center"
          gap="12px"
          justifyContent="space-between"
        >
          <Text
            color="neutral.primary"
            id={`${id}__title`}
            variant="bodyMedium"
          >
            {title}
          </Text>
          <ChakraFlex alignItems="center" flexShrink={0} gap="12px">
            {badge}
            {avatarProps && <Avatar {...avatarProps} id={id} size="xs" />}
          </ChakraFlex>
        </ChakraFlex>
        <ChakraFlex
          alignItems="center"
          columnGap="18px"
          flexWrap="wrap"
          rowGap="2px"
        >
          <AppointmentInfo
            icon={FAIconName.calendarRegular}
            id={`${id}__date`}
            label={dateLabel}
          />
          <AppointmentInfo
            icon={FAIconName.clockRegular}
            id={`${id}__time`}
            label={timeLabel}
          />
          {recurrenceLabel && (
            <AppointmentInfo
              icon={FAIconName.repeatRegular}
              id={`${id}__recurrence`}
              label={recurrenceLabel}
            />
          )}
        </ChakraFlex>
      </ChakraFlex>
    </ChakraBox>
  );
};

export default AppointmentCard;
